// src/components/loads/hooks/useLoadsData.js
import { useState, useEffect, useMemo } from 'react';
import { db } from '../../../firebase';
import { collection, query, where, onSnapshot, orderBy, limit } from "firebase/firestore";
import { DEFAULT_CURRENT_STATUSES, ALL_STATUSES } from '../utils/constants';
import { determineTenantId, filterLoadsBySearch } from '../utils/loadHelpers';
import { getViewerScope } from '../../../utils/companyScope';

const LOADS_PER_PAGE = 30;

// ============================================================================
// HELPER: Check if user is Super Admin
// ============================================================================
const isSuperAdmin = (user) => {
  if (!user) return false;
  const roles = Array.isArray(user.role) ? user.role : [user.role].filter(Boolean);
  return roles.includes('Super Admin');
};

/**
 * Custom hook to fetch and manage loads data with pagination
 * PHASE 2: Non-Super Admin users only see loads belonging to their parent companies
 * Note: parentCompanyId filtering is done CLIENT-SIDE because Firestore
 * doesn't allow two 'in' operators in the same query (status already uses 'in')
 * @param {Object} loggedInUser - Current logged in user
 * @param {Object} filters - Active filters
 * @param {string} applicationTimeZone - Application timezone
 * @param {boolean} isLoadingTimeZone - Whether timezone is still loading
 * @param {number} loadLimit - Number of loads to display (for pagination)
 * @returns {Object} Loads data and state
 */
export const useLoadsData = (loggedInUser, filters, applicationTimeZone, isLoadingTimeZone, loadLimit = LOADS_PER_PAGE, canSeeLoad = null) => {
  const [allLoads, setAllLoads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const currentTenantId = determineTenantId(loggedInUser);

    // Who may see which load is decided by utils/loadAccess (canSeeLoad, applied
    // below before paging). Here only the "assigned to nothing" case stops early.
    const userHasNoCompanies = getViewerScope(loggedInUser, isSuperAdmin(loggedInUser)).seesNothing;

    if (!loggedInUser || !loggedInUser.uid || isLoadingTimeZone) { 
      console.log("Loads Fetch Effect: Conditions not met", {
        loggedInUser: !!loggedInUser,
        isLoadingTimeZone
      });
      setAllLoads([]); 
      setIsLoading(false);
      return; 
    }

    if (!currentTenantId) {
      console.error("❌ No tenant ID found for user. Cannot load tenant-specific data.");
      setError("Tenant information missing. Please contact administrator.");
      setIsLoading(false);
      return;
    }

    // If non-Super Admin has no assigned companies, show nothing
    if (userHasNoCompanies) {
      console.warn("⚠️ Non-Super Admin user has no assigned parent companies. Showing no loads.");
      setAllLoads([]);
      setIsLoading(false);
      return;
    }

    console.log("Loads Fetch Effect: Fetching current loads. Filters:", filters);
    setError(null);
    setIsLoading(true);

    let conditions = [];
    
    // Build query conditions
    const statusArray = filters.showCompleted ? ALL_STATUSES : DEFAULT_CURRENT_STATUSES;
    
    if (filters.status !== 'all') {
      conditions.push(where("status", "==", filters.status));
    } else {
      conditions.push(where("status", "in", statusArray));
    }
    
    // Driver / truck / broker / dispatcher are filtered in the browser, not in
    // the query.
    //
    // Each one added to the query needs its own composite index alongside
    // tenantId, isArchived, status and createdAt — and in combination that is
    // sixteen indexes to cover every way the filter bar can be set. Missing one
    // does not degrade quietly: Firestore refuses the query outright and the
    // page shows "Error loading loads", which is exactly what happened to live
    // users when isArchived was added to the query.
    //
    // Doing it here costs nothing. With archived loads excluded the result set
    // is a few hundred rows, and filtering that in memory is instant. Only
    // tenantId, isArchived and status stay server-side: those are what make the
    // set small in the first place.

    try {
      const queryParts = [
        collection(db, "loads"),
        where("tenantId", "==", currentTenantId),
        // Archived loads are excluded HERE rather than in the browser.
        //
        // They used to be fetched and then thrown away, which quietly cost
        // people their work: archiving is normal once a load is paid, so the
        // archive pile vastly outnumbers live loads — 1,513 against 310 on the
        // largest account. Every archived load still occupied a place in the
        // "most recent N" window, so the window never reached far enough back.
        // A Main Admin capped at 500 saw 187 of 310 live loads; a Super Admin
        // capped at 1,000 saw 214. Nobody saw all of them, and the ones that
        // fell off were simply the oldest — still Booked, still In Transit,
        // still real work.
        //
        // Filtering server-side means the window is spent entirely on live
        // loads, so the cap stops mattering and every role sees the same set.
        //
        // Every load carries this field: new ones are created with
        // isArchived: false, and the handful of older ones that predated the
        // field were backfilled. That matters because a where() clause skips
        // documents missing the field altogether — they would have vanished.
        where("isArchived", "==", false),
        ...conditions,
        orderBy("createdAt", "desc")
      ];

      // One cap for every role. Differing caps were how a Main Admin ended up
      // seeing less than a Super Admin of the same tenant's live work — their
      // two tabs could never add up to the whole.
      const MAX_LOADS = 1000;
      queryParts.push(limit(MAX_LOADS));

      const q = query(...queryParts);

      const unsubscribe = onSnapshot(
        q, 
        (snapshot) => {
          console.log("🔍 DEBUG: Received loads snapshot:", {
            totalDocs: snapshot.docs.length,
            tenantId: currentTenantId,
            showCompleted: filters.showCompleted
          });

          let loads = snapshot.docs.map(doc => ({
            docId: doc.id,
            ...doc.data()
          }));

          // The filter-bar selections, applied here rather than in the query —
          // see the note where the query is built. Same result, no index per
          // combination.
          if (filters.driverId !== 'all') {
            loads = loads.filter(l => l.driverId === filters.driverId);
          }
          if (filters.truckId !== 'all') {
            loads = loads.filter(l => l.truckId === filters.truckId);
          }
          if (filters.brokerId !== 'all') {
            loads = loads.filter(l => l.brokerId === filters.brokerId);
          }
          if (filters.dispatcherId !== 'all') {
            loads = loads.filter(l => l.dispatcherId === filters.dispatcherId);
          }

          // ✅ ALWAYS filter out archived loads
          loads = loads.filter(load => {
            const isArchived = load.isArchived === true;
            if (isArchived) {
              console.log('🗑️ Filtering out archived load:', load.load_id);
            }
            return !isArchived;
          });

          // Filter for unassigned loads if checkbox is checked
          const finalLoads = filters.showUnassignedOnly 
            ? loads.filter(load => !load.driverId || load.driverId === '')
            : loads;

          console.log("🔍 DEBUG: Final loads count:", finalLoads.length);

          setAllLoads(finalLoads);
          setIsLoading(false);
        }, 
        (err) => {
          console.error("❌ Loads Fetch Effect: Error fetching loads:", err);
          setError("Failed to fetch loads. " + (err?.message || "Ensure Firestore indexes are set up."));
          setIsLoading(false);
        }
      );

      return () => {
        console.log("Loads Fetch Effect: Cleaning up loads listener.");
        unsubscribe();
      };
    } catch (err) {
      console.error("❌ Loads Fetch Effect: Error constructing query:", err);
      setError("Error constructing query. Please check filter logic.");
      setIsLoading(false);
    }
  }, [loggedInUser, filters, isLoadingTimeZone, applicationTimeZone]);

  // Who may see what, then the Load ID search. Both before paging, so every
  // page of 30 is 30 loads this viewer actually works with.
  const filteredLoads = useMemo(() => {
    const visible = canSeeLoad ? allLoads.filter(canSeeLoad) : allLoads;
    return filterLoadsBySearch(visible, filters.searchLoadId);
  }, [allLoads, canSeeLoad, filters.searchLoadId]);

  // Apply pagination limit (only if NOT searching by Load ID)
  const displayedLoads = useMemo(() => {
    if (filters.searchLoadId && filters.searchLoadId.trim() !== '') {
      return filteredLoads;
    }
    return filteredLoads.slice(0, loadLimit);
  }, [filteredLoads, loadLimit, filters.searchLoadId]);

  const hasMoreLoads = filteredLoads.length > displayedLoads.length;

  const MAX_LOADS = isSuperAdmin(loggedInUser) ? 1000 : 500;

  return {
    currentLoads: displayedLoads,
    isLoading,
    error,
    totalLoads: allLoads.length,
    filteredCount: filteredLoads.length,
    displayedCount: displayedLoads.length,
    hasMoreLoads,
    LOADS_PER_PAGE,
    MAX_LOADS
  };
};